import { Phone, MapPin, CheckCircle, Clock, ArrowRight, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const TowerHamletsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Tower Hamlets | Vehicle Transport E1, E3, E14 | 24/7 Breakdown"
        description="Fast car recovery in Tower Hamlets E1, E3, E14. 30-minute response. 24/7 breakdown service, Canary Wharf, Whitechapel. Call 07492 639223 for Tower Hamlets recovery."
        keywords="car recovery tower hamlets, breakdown service tower hamlets, car recovery e1, car recovery e3, e14 vehicle transport, canary wharf car recovery, whitechapel breakdown"
      />
      <Header />

      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🏙️ Tower Hamlets Specialists
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Recovery & Vehicle Transport in Tower Hamlets
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Fast 30-minute car recovery across Tower Hamlets E1, E3, E14. Available 24/7 for breakdowns in Canary Wharf, Whitechapel, Bow, Mile End, and all Tower Hamlets areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">30 Min</p>
              <p className="text-sm text-slate-600">Avg Response</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">24/7</p>
              <p className="text-sm text-slate-600">Always Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">E1-E14</p>
              <p className="text-sm text-slate-600">All Postcodes</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">Local</p>
              <p className="text-sm text-slate-600">East London</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Car Recovery in Tower Hamlets
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos provides fast, professional car recovery and vehicle transport across all Tower Hamlets areas. From Canary Wharf to Whitechapel, Bow to Mile End, our local team delivers an average 30-minute response time for all breakdown and emergency recovery situations.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Tower Hamlets Areas We Cover
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Canary Wharf E14",
                "Whitechapel E1",
                "Bow E3",
                "Mile End E3",
                "Stepney E1",
                "Limehouse E14",
                "Poplar E14",
                "Isle of Dogs E14",
                "Bethnal Green E2",
                "Aldgate E1",
                "Wapping E1W",
                "Shadwell E1"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Tower Hamlets Recovery Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "24/7 emergency breakdown recovery",
                "Vehicle transportation service",
                "Canary Wharf car park recovery",
                "Jump start and battery services",
                "Accident and collision recovery",
                "Emergency roadside assistance",
                "Flat tyre recovery",
                "Out of fuel delivery",
                "Non-running vehicle transport",
                "Locked out assistance",
                "Car auction transport",
                "Corporate fleet recovery"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Us for Tower Hamlets Car Recovery?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Building2 className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Canary Wharf Specialists</h4>
                      <p className="text-slate-600">
                        We navigate Canary Wharf's complex underground car parks, security gates, and restricted zones daily. We know all access points for fast recovery.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">30-Minute Response Time</h4>
                      <p className="text-slate-600">
                        Based locally in East London with quick access to all Tower Hamlets postcodes. Faster response than national recovery services.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">24/7 Service - Fixed Pricing</h4>
                      <p className="text-slate-600">
                        Available every day including weekends and bank holidays. Transparent pricing with no hidden fees. Same rates 24/7 - no night surcharges.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-blue-900 to-indigo-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Car Recovery in Tower Hamlets?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            24/7 car recovery and vehicle transport service across Tower Hamlets E1, E3, E14 including Canary Wharf.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6">
                Get Quote Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
